import React, { useEffect, useState } from 'react';
import { BasicSelectProps, Keyboard, Select as SelectInput, SelectMultiple, Spinner, Stack } from 'grommet';
import { observer } from 'mobx-react-lite';
import { Box, BoxProps, Input, Line, Text } from '/src/components';
import { SelectOption } from '/src/lib/types';
import { pxToRem } from '/src/utils';

export type SelectProps = BoxProps & {
  label: string;
  value?: string | number | null | undefined;
  setValue?: (value: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  options: (SelectOption<string | number | null | undefined> | string)[];
  onMore?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  onSearch?: (query: string) => void;
  onChange?: (props: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  onSubmit?: (event: React.KeyboardEvent<HTMLElement>) => void;
  error?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  required?: boolean;
  fuseAdminOnly?: boolean;
  hideOptionalText?: boolean;
  hideRequiredMarker?: boolean;
  hideLabel?: boolean;
  disabled?: BasicSelectProps['disabled'];
  multiple?: boolean;
  isLoadingOptions?: boolean;
  clear?: boolean;
  hideSelectAll?: boolean;
  values?: string[] | number[];
  setValues?: (values: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  searchPlaceholder?: string;
  emptySearchMessage?: string;
  labelGap?: boolean;
};

export const Select: React.FC<SelectProps> = observer((props) => {
  const {
    label,
    value,
    setValue,
    error,
    id,
    name,
    onChange,
    onSubmit,
    placeholder,
    required,
    fuseAdminOnly,
    hideOptionalText,
    hideRequiredMarker,
    hideLabel,
    disabled,
    onOpen,
    onClose,
    onMore,
    onSearch,
    options,
    multiple,
    isLoadingOptions,
    clear,
    hideSelectAll,
    values,
    setValues,
    searchPlaceholder = 'Search...',
    emptySearchMessage = 'No matches found.',
    labelGap,
    ...boxProps
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<(SelectOption<string | number | null | undefined> | string)[]>(
    []
  );
  const shouldShowSearch = onSearch ? true : options.length > 5;
  const isLoading = !!isLoadingOptions;

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleClose = () => {
    setFilteredOptions(options);
    if (onClose) onClose();
  };

  const blockWhileLoading = (event: React.SyntheticEvent) => {
    if (isLoading) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const handleSearch = (query: string) => {
    if (query === '') {
      setFilteredOptions(options);
      return;
    }

    const exp = new RegExp(query.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
    const filtered = options.filter((option) => {
      if (typeof option === 'string') {
        return exp.test(option);
      } else {
        return exp.test(option.label);
      }
    });
    setFilteredOptions(filtered);
  };

  const selectIcon = isLoading ? <Spinner size="xsmall" /> : undefined;

  let disabledValue: string | undefined;

  if (disabled) {
    const option = options.find((o) => {
      if (typeof o === 'object') {
        return o.value === value;
      } else {
        return o === value;
      }
    });
    if (typeof option === 'object') disabledValue = option.label;
  }

  const renderSelect = () => (
    <SelectInput
      id={id}
      name={name || id}
      placeholder={placeholder || 'Choose...'}
      labelKey="label"
      valueKey={{ key: 'value', reduce: true }}
      value={value ?? undefined}
      onChange={onChange ? onChange : ({ value: nextValue }) => (setValue ? setValue(nextValue) : undefined)}
      plain
      style={{
        fontFamily: 'Lato, sans-serif',
        height: !hideLabel ? pxToRem(58) : undefined,
        paddingTop: !hideLabel ? pxToRem(26) : undefined,
        paddingBottom: !hideLabel ? pxToRem(10) : undefined,
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onMore={onMore}
      onOpen={onOpen}
      onClose={handleClose}
      onSearch={shouldShowSearch ? onSearch || handleSearch : undefined}
      clear={clear ? { label: 'Clear Selection' } : undefined}
      options={filteredOptions}
      required={required}
      disabled={disabled}
      icon={selectIcon}
      searchPlaceholder={searchPlaceholder}
      emptySearchMessage={emptySearchMessage}
      dropHeight={pxToRem(300)}
    />
  );

  const isTotallyDisabled = typeof disabled === 'boolean' ? disabled : disabled?.length === filteredOptions.length;

  const renderSelectMultiple = () => (
    <SelectMultiple
      id={id}
      name={name || id}
      placeholder={placeholder || 'Choose...'}
      labelKey="label"
      valueKey={{ key: 'value', reduce: true }}
      value={values}
      onChange={({ value: nextValues }) => (setValues ? setValues(nextValues) : undefined)}
      plain
      style={{
        fontFamily: 'Lato, sans-serif',
        height: pxToRem(58),
        paddingTop: pxToRem(26),
        paddingBottom: pxToRem(10),
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onMore={onMore}
      onOpen={onOpen}
      onClose={handleClose}
      onSearch={shouldShowSearch ? onSearch || handleSearch : undefined}
      options={filteredOptions}
      limit={hideSelectAll ? options.length : undefined}
      required={required}
      disabled={disabled}
      icon={selectIcon}
      searchPlaceholder={searchPlaceholder}
      emptySearchMessage={emptySearchMessage}
      dropHeight={pxToRem(300)}
    />
  );

  const renderSelectComponent = () => (
    <Box onClickCapture={blockWhileLoading} onKeyDownCapture={blockWhileLoading} aria-busy={isLoading || undefined}>
      <Keyboard onEnter={onSubmit}>{multiple ? renderSelectMultiple() : renderSelect()}</Keyboard>
    </Box>
  );

  const borderColor = error ? 'red' : isFocused ? 'accent-1' : 'light-5';

  return !!disabled && !multiple ? (
    <Box {...boxProps}>
      <Input
        id={id}
        name={name || id}
        label={label}
        value={disabledValue ?? value ?? ''}
        placeholder={placeholder}
        required={required}
        hideOptionalText={hideOptionalText}
        disabled
      />
    </Box>
  ) : (
    <Box {...boxProps}>
      <Box
        border={isTotallyDisabled ? undefined : { color: borderColor, size: 'small' }}
        background={isTotallyDisabled ? 'light-6' : undefined}
        borderRadius="6px"
      >
        {!hideLabel ? (
          <Stack anchor="top-left" guidingChild="last" fill="vertical">
            <Box pad={{ top: 'xsmall', left: 'small' }}>
              <Text size="small" color={isTotallyDisabled ? 'light-3' : 'accent-1'}>
                {label}
                {required && !hideRequiredMarker && '*'}
              </Text>
            </Box>
            <Box pad={{ top: labelGap ? 'xsmall' : undefined }}>{renderSelectComponent()}</Box>
          </Stack>
        ) : (
          <Box>{renderSelectComponent()}</Box>
        )}
      </Box>
      {(error || fuseAdminOnly) && (
        <Box row gap="small" margin={{ top: '0.5rem', left: '0.75rem' }}>
          {error && (
            <Text color="red" size="0.75rem">
              {error}
            </Text>
          )}
          {error && fuseAdminOnly && <Line direction="vertical" margin="0" />}
          {fuseAdminOnly && (
            <Text color="dark-1" size="0.75rem">
              FuSE ADMINS ONLY
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
});
